import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';

import { getInitialState } from './getInitialState.ts';
import { updateSSRSmartLinksStore, resetSSRSmartLinksStore } from './ssrSmartLinksStore.ts';
import type { SmartLinkType } from './getSmartLinks.ts';
import {
	preloadContentSmartlinksQuery,
	fetchORSForSSRWithTimeout,
	SMART_LINK_INDIVIDUAL_TIMEOUT,
	SMART_LINK_BATCH_TIMEOUT,
	DEFAULT_MIN_BATCH_COUNT,
	DEFAULT_MAX_BATCH_COUNT,
} from './preloadContentSmartLinks.ts';
import {
	groupSmartLinksByHostname,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
	isFirstPartyLink,
} from './smartLinkBatchHelpers.ts';

const DEFAULT_DATA_FETCHER_TIMEOUT = 1000;
const SMARTLINKS_TIMEOUT_BUFFER = 250;

/*
 * Statsig timeout configs for preloading with suspense
 */
function getStreamingTimeoutsAndBatchSizes() {
	// How long a single ORS resolution can take
	const timeout = StatsigConfigurations.getNumber(
		'confluence_smart_link_streaming_max_time',
		SMART_LINK_INDIVIDUAL_TIMEOUT,
	);

	// How long a batch of ORS resolution can take
	const batchTimeout = StatsigConfigurations.getNumber(
		'confluence_smart_link_streaming_batch_max_time',
		SMART_LINK_BATCH_TIMEOUT,
	);

	// How many links should be packed in a batch
	const min = StatsigConfigurations.getNumber(
		'confluence_smart_link_min_batch_count',
		DEFAULT_MIN_BATCH_COUNT,
	);

	const max = Math.max(
		min,
		StatsigConfigurations.getNumber(
			'confluence_smart_link_streaming_max_batch_count',
			DEFAULT_MAX_BATCH_COUNT,
		),
	);

	return { timeout, batchTimeout, min, max };
}

// merge links when batches resolve into global store
function mergeIntoWindowSSRSmartlinks(resolvedSubset: any[]) {
	const subsetState = getInitialState(resolvedSubset);
	updateSSRSmartLinksStore(subsetState);
}

/**
 * Get batches for a given bucket (1p or 3p)
 */
function getBatchesForBucket(config: BatchDataFetchersConfig): SmartLinkType[][] {
	const { links, isThirdParty, min, max } = config;
	if (!links.length) {
		return [];
	}

	// mirroring batching logic in preloadContentSmartLinks.ts
	let smartLinksBatches = groupSmartLinksByHostname(links);
	// only doing 3p split in the case where we know link is 3p
	if (isThirdParty) {
		smartLinksBatches = mergeSmallThirdPartyBatches(smartLinksBatches, min);
	}
	smartLinksBatches = splitLinksIntoBatches(smartLinksBatches, max);
	return smartLinksBatches;
}

interface BatchDataFetchersConfig {
	bucketName: string;
	isThirdParty: boolean;
	links: SmartLinkType[];
	min: number;
	max: number;
}

/**
 * createBatchDataFetchersForBucket
 *
 * Loops through buckets for each bucket (1p vs. 3p) and creates data fetchers
 * for each ORS batch call
 */
export function createBatchDataFetchersForBucket(config: BatchDataFetchersConfig) {
	const batches = getBatchesForBucket(config);
	const { bucketName } = config;

	batches.forEach((batch, index) => {
		const fetcherName = `content-smartlinks-${bucketName}-batch-${index}`;

		// Use the global request start time as the source of truth for the clock
		const requestStartTime = window['__SSR_CURRENT_DATE__'] || performance.now();

		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		batch.forEach((link) => {
			if (window.__SSR_SMARTLINKS_BOUNDARY_MAP__) {
				window.__SSR_SMARTLINKS_BOUNDARY_MAP__[link.resourceUrl] = fetcherName;
			}
		});
		createDataFetcherForSuspense(fetcherName, async () => {
			// batchTimeout should almost equal (but be slightly less than) data fetcher timeout
			const { timeout, batchTimeout } = getStreamingTimeoutsAndBatchSizes();
			const dataFetcherTimeout = StatsigConfigurations.getNumber(
				'confluence_frontend_data_fetcher_timeout',
				DEFAULT_DATA_FETCHER_TIMEOUT,
			);
			const fetchTimeout: Record<string, number> = {};
			const resolved = await fetchORSForSSRWithTimeout(
				batch,
				timeout,
				batchTimeout,
				fetchTimeout,
				true,
			);

			// Calculate time since the absolute start of the request
			const totalTimeSinceRequestStart = performance.now() - requestStartTime;

			const dataFetcher = window.__dataFetcherForSuspense__?.[fetcherName];
			/**
			 * Even if the data fetcher hasn't officially timed out, check the total elapsed time.
			 * If it is too close to the data fetcher timeout (within a buffer time), don't merge the results.
			 *
			 * This avoids a race condition where ORS returns at the exact same time/after the
			 * fetcher timeout, which leads to a hydration error if the fallback is committed first.
			 *
			 * Buffer time ensures React has enough time to render
			 * the results into HTML before guard timeout resolves the boundary.
			 */
			if (
				(dataFetcher && dataFetcher.isTimeout()) ||
				totalTimeSinceRequestStart >= dataFetcherTimeout - SMARTLINKS_TIMEOUT_BUFFER
			) {
				return [];
			}

			mergeIntoWindowSSRSmartlinks(resolved);
			return resolved;
		});
	});
}

/**
 * preloadContentSmartLinksWithSuspense waits for GraphQL call to fetch smartlinks,
 * then creates data fetches in batches on a per-party basis (1p vs. 3p)
 *
 * Data fetchers are only created for batches so as to allow for individual batches to resolve
 * independent of each other
 */
export async function preloadContentSmartLinksWithSuspense(contentId: string, isDraft?: boolean) {
	// Clear old data from the store
	resetSSRSmartLinksStore();
	window.__SSR_SMARTLINKS_BOUNDARY_MAP__ = {};

	// Wait for GraphQL first to know how many links we have
	const smartLinks = await preloadContentSmartlinksQuery(contentId, isDraft);
	if (!smartLinks?.length) {
		return;
	}

	// De-duplicate links to ensure each unique URL belongs to only one batch.
	// This prevents hydration mismatches where a link resolves in a successful
	// batch but waits on a pending boundary from a duplicate entry in another batch.
	const uniqueLinks = Array.from(
		new Map(smartLinks.map((link) => [link.resourceUrl, link])).values(),
	);

	const firstPartyLinks = uniqueLinks.filter((link) => isFirstPartyLink(link.resourceUrl));
	const thirdPartyLinks = uniqueLinks.filter((link) => !isFirstPartyLink(link.resourceUrl));

	const { min, max } = getStreamingTimeoutsAndBatchSizes();

	// create data fetchers only when we know buckets have links
	if (firstPartyLinks.length) {
		createBatchDataFetchersForBucket({
			bucketName: 'first-party',
			isThirdParty: false,
			links: firstPartyLinks,
			min,
			max,
		});
	}

	if (thirdPartyLinks.length) {
		createBatchDataFetchersForBucket({
			bucketName: 'third-party',
			isThirdParty: true,
			links: thirdPartyLinks,
			min,
			max,
		});
	}
}
